// The battlefield's ground, painted rather than flat.
//
// The ground was one flat quad of #1b2430 with a slightly darker strip
// near edge for the graveyard. Two problems with that, and neither is
// cosmetic:
//
//   - no scale cue. Orbiting a single uniform colour gives no sense of
//     movement or size, so the board reads as a diagram rather than as a
//     place.
//   - no region legibility. The only way to tell the boss keep from the
//     field from the graveyard was to look at what happened to be
//     standing on them, which defeats the point of having bands at all.
//
// So the base is painted: a tone per band, a grid for scale and parallax,
// and a falloff at the edges. One texture on the material the ground
// already had, so it costs no draw calls — and it absorbs the graveyard
// strip, so it costs one fewer.
//
// Painted rather than loaded because the project self-hosts and ships no
// binary assets, and because a procedural base follows the layout if the
// layout moves.

import * as THREE from 'three';
import {
  BOSS_Z,
  FIELD_Z_FAR,
  FIELD_Z_NEAR,
  GRAVE_Z_FAR,
  GOAL_Z,
  PLANE_X_HALF,
  PLANE_Z_FAR,
  PLANE_Z_NEAR,
  REINFORCE_Z_FAR,
  REINFORCE_Z_NEAR,
} from './layout.js';

/** World extent of the painted plane. */
export const BASE_WIDTH = PLANE_X_HALF * 2;
export const BASE_DEPTH = PLANE_Z_NEAR - PLANE_Z_FAR;

export type RegionId = 'keep' | 'camps' | 'field' | 'reinforce' | 'graves';

export interface BasemapRegion {
  id: RegionId;
  /** World z where the band starts, inclusive (far edge). */
  from_z: number;
  /** World z where the band ends, exclusive (near edge). */
  to_z: number;
  colour: string;
}

/**
 * The bands, far to near.
 *
 * Boundaries are MIDPOINTS between the landmarks layout.ts places, never
 * hardcoded z values. Move the boss keep and the ground follows without
 * anyone remembering to repaint it — a base that disagrees with where the
 * pieces stand is worse than a plain one.
 */
export function basemapRegions(): BasemapRegion[] {
  const keepTo = (BOSS_Z + GOAL_Z) / 2;
  const campsTo = (GOAL_Z + FIELD_Z_FAR) / 2;
  const fieldTo = (FIELD_Z_NEAR + REINFORCE_Z_FAR) / 2;
  const reinforceTo = (REINFORCE_Z_NEAR + GRAVE_Z_FAR) / 2;
  return [
    // Nearest the enemy. Colder and rustier: this is where the fight is
    // and it should not read like home ground.
    { id: 'keep', from_z: PLANE_Z_FAR, to_z: keepTo, colour: '#1a1418' },
    // The encampments. Warm, because the goals are the things we chose.
    { id: 'camps', from_z: keepTo, to_z: campsTo, colour: '#1d1a13' },
    // The contested middle, and the largest band. The original ground.
    { id: 'field', from_z: campsTo, to_z: fieldTo, colour: '#1b2430' },
    // Arrived mid-sprint. Tinted toward the orange those pieces are, so
    // the ground and the things standing on it agree.
    { id: 'reinforce', from_z: fieldTo, to_z: reinforceTo, colour: '#231a12' },
    // The graveyard. Green, as it has been.
    { id: 'graves', from_z: reinforceTo, to_z: PLANE_Z_NEAR, colour: '#141a12' },
  ];
}

/** Which band a world z falls in. Far edge inclusive. */
export function regionAt(z: number): RegionId {
  for (const r of basemapRegions()) {
    if (z >= r.from_z && z < r.to_z) return r.id;
  }
  return z < PLANE_Z_FAR ? 'keep' : 'graves';
}

/** World (x, z) to canvas pixels.
 *
 *  The plane is rotated -PI/2 about x, so it runs far edge to near edge
 *  down the canvas. Derivation, so nobody has to redo it: after that
 *  rotation local +y becomes world -z, and flipY inverts again, which is
 *  why this is a direct proportion rather than an inverse one.
 */
export function worldToCanvas(
  x: number,
  z: number,
  widthPx: number,
  heightPx: number,
): { x: number; y: number } {
  return {
    x: ((x + BASE_WIDTH / 2) / BASE_WIDTH) * widthPx,
    y: ((z - PLANE_Z_FAR) / BASE_DEPTH) * heightPx,
  };
}

/** World units between grid lines. */
const GRID_STEP = 8;
/** Every Nth line is drawn brighter, so distance is countable. */
const MAJOR_EVERY = 4;

export function createBasemapTexture(): THREE.CanvasTexture {
  // Height is derived from the world aspect ratio so a grid cell stays
  // square. A 1:1 canvas would paint a subtly rectangular grid, which
  // looks wrong without being obvious why.
  const widthPx = 1024;
  const heightPx = Math.round((widthPx * BASE_DEPTH) / BASE_WIDTH);
  const canvas = document.createElement('canvas');
  canvas.width = widthPx;
  canvas.height = heightPx;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('basemap: no 2d context');

  // --- region bands, with soft seams ------------------------------
  const regions = basemapRegions();
  for (const r of regions) {
    const top = worldToCanvas(0, r.from_z, widthPx, heightPx).y;
    const bottom = worldToCanvas(0, r.to_z, widthPx, heightPx).y;
    ctx.fillStyle = r.colour;
    ctx.fillRect(0, top, widthPx, bottom - top);
  }
  for (const r of regions.slice(1)) {
    // A short vertical gradient at each seam. A hard line would read as a
    // wall; terrain changes gradually.
    const y = worldToCanvas(0, r.from_z, widthPx, heightPx).y;
    const g = ctx.createLinearGradient(0, y - 26, 0, y + 26);
    g.addColorStop(0, 'rgba(0,0,0,0)');
    g.addColorStop(0.5, 'rgba(0,0,0,0.30)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, y - 26, widthPx, 52);
  }

  // --- grid -------------------------------------------------------
  ctx.lineWidth = 1;
  let i = 0;
  for (let x = -BASE_WIDTH / 2; x <= BASE_WIDTH / 2; x += GRID_STEP, i += 1) {
    const px = Math.round(worldToCanvas(x, 0, widthPx, heightPx).x) + 0.5;
    ctx.strokeStyle = i % MAJOR_EVERY === 0 ? 'rgba(255,255,255,0.055)' : 'rgba(255,255,255,0.022)';
    ctx.beginPath();
    ctx.moveTo(px, 0);
    ctx.lineTo(px, heightPx);
    ctx.stroke();
  }
  i = 0;
  for (let z = PLANE_Z_FAR; z <= PLANE_Z_NEAR; z += GRID_STEP, i += 1) {
    const py = Math.round(worldToCanvas(0, z, widthPx, heightPx).y) + 0.5;
    ctx.strokeStyle = i % MAJOR_EVERY === 0 ? 'rgba(255,255,255,0.055)' : 'rgba(255,255,255,0.022)';
    ctx.beginPath();
    ctx.moveTo(0, py);
    ctx.lineTo(widthPx, py);
    ctx.stroke();
  }

  // --- edge falloff ----------------------------------------------
  // The board ends at a hard rectangle otherwise, which reads as a decal
  // floating in space rather than as ground continuing past where we
  // bothered to draw it.
  const side = ctx.createLinearGradient(0, 0, widthPx, 0);
  side.addColorStop(0, 'rgba(0,0,0,0.55)');
  side.addColorStop(0.09, 'rgba(0,0,0,0)');
  side.addColorStop(0.91, 'rgba(0,0,0,0)');
  side.addColorStop(1, 'rgba(0,0,0,0.55)');
  ctx.fillStyle = side;
  ctx.fillRect(0, 0, widthPx, heightPx);

  // Distance haze at the far edge: the keep is meant to feel far away.
  const haze = ctx.createLinearGradient(0, 0, 0, heightPx * 0.28);
  haze.addColorStop(0, 'rgba(5,7,10,0.55)');
  haze.addColorStop(1, 'rgba(5,7,10,0)');
  ctx.fillStyle = haze;
  ctx.fillRect(0, 0, widthPx, heightPx * 0.28);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  // The grid is read at a glancing angle most of the time; without
  // mipmaps it shimmers while the board is orbited.
  tex.generateMipmaps = true;
  tex.minFilter = THREE.LinearMipmapLinearFilter;
  // Anisotropy 1, not 4. The grid is read at a glancing angle and
  // anisotropic filtering is what sharpens it there — but it multiplies
  // the texture-fetch cost per fragment, and the ground covers most of the
  // screen. Mipmaps already deal with the aliasing; the extra sharpness is
  // not worth a fifth of the frame.
  tex.anisotropy = 1;
  return tex;
}
